import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHourlyAbsenceAmountFormatter } from '../../../hooks/useHourlyAbsenceAmountFormatter';
import { AbsenceType } from '../../../__generated__/graphql';

type Variant = 'normal' | 'work' | 'calendar';

type Props = {
  type: AbsenceType;
  amount: number;
  variant: Variant;
};

const DailyAbsenceAmount: React.FC<
  React.PropsWithChildren<Pick<Props, 'amount' | 'variant'>>
> = ({ amount, variant }) => {
  const { t } = useTranslation();
  const roundedAmount = Math.round(amount * 100) / 100; // round to 2 decimal places
  switch (variant) {
    case 'normal': {
      return <>{t('absences.days', { count: roundedAmount })}</>;
    }
    case 'work': {
      return <>{t('absences.workingDays', { count: roundedAmount })}</>;
    }
    case 'calendar': {
      return <>{t('absences.calendarDays', { count: roundedAmount })}</>;
    }
  }
};

const HourlyAbsenceAmount: React.FC<
  React.PropsWithChildren<Pick<Props, 'amount'>>
> = ({ amount }) => {
  const { t } = useTranslation();
  const hourlyFormatter = useHourlyAbsenceAmountFormatter();

  return <>{t('absences.hours', { amount: hourlyFormatter(amount) })}</>;
};

const AbsenceAmount: React.FC<React.PropsWithChildren<Props>> = ({
  type,
  amount,
  variant,
}) => {
  switch (type) {
    case AbsenceType.Daily:
      return <DailyAbsenceAmount amount={amount} variant={variant} />;
    case AbsenceType.Hourly: {
      return <HourlyAbsenceAmount amount={amount} />;
    }
    case AbsenceType.Unknown:
      return <>{amount}</>;
  }
};

export default AbsenceAmount;
